import '../styles/LazyLoad.css';
import React, { useState, useContext, useEffect } from 'react';
import { FaTree, FaVideo, FaChevronLeft, FaChevronRight, FaTimes, FaSeedling, FaSpinner } from 'react-icons/fa';
import { agricultureImages } from '../data/agricultureImages';
import '../styles/AgricultureGallery.css';
import '../media-query/agrigalley.css';
import { LanguageContext } from '../context/LanguageContext';

const AgricultureGallery = () => {
  const [activeCategory, setActiveCategory] = useState('farming');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const { translations } = useContext(LanguageContext);

  const t = translations.AgricultureGallery;

  const galleryData = {
    farming: {
      title: t.galleryTitles?.farming || 'Farming & Agriculture',
      icon: <FaSeedling />,
      images: agricultureImages.farming?.images || [],
    },  
    videos: {
      title: t.galleryTitles?.videos || 'Agriculture Videos',
      icon: <FaVideo />,
      videos: [],
      images: [],
    },
  };

  const categories = [
    { key: 'farming', icon: <FaSeedling /> },
    { key: 'videos', icon: <FaVideo /> },
  ];

  const currentContent = galleryData[activeCategory];

  // Show skeleton only briefly, then show images
  useEffect(() => {
    setShowSkeleton(true);
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [activeCategory]);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrev = () => {
    const images = currentContent.images;
    if (!images || images.length === 0) return;
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const goToNext = () => {
    const images = currentContent.images;
    if (!images || images.length === 0) return;
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrev();
    if (e.key === 'ArrowRight') goToNext();
  };

  useEffect(() => {
    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImage, currentIndex]);

  const getCategoryLabel = (key) => {
    if (t.categories && t.categories[key]) {
      return t.categories[key];
    }
    const labels = {
      'farming': 'Farming & Agriculture',
      'videos': 'Agriculture Videos'
    };
    return labels[key] || key;
  };

  // Skeleton loader component
  const SkeletonLoader = ({ count = 6 }) => {
    return (
      <div className="agriculture-images-grid">
        {Array(count).fill(0).map((_, index) => (
          <div key={`skeleton-${index}`} className="agriculture-image-card skeleton-card">
            <div className="skeleton-image"></div>
            <div className="skeleton-shimmer"></div>
          </div>
        ))}
      </div>
    );
  };

  // Image component - shows immediately with placeholder
  const GalleryImage = ({ src, alt, onClick }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    return (
      <div className="agriculture-image-card" onClick={onClick}>
        {!loaded && !error && (
          <div className="image-placeholder">
            <FaSpinner className="spinner" />
          </div>
        )}
        {error && (
          <div className="image-error">
            <FaSeedling />
            <span>Failed to load</span>
          </div>
        )}
        <img 
          src={src} 
          alt={alt}
          className="agriculture-gallery-image"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          loading="lazy"
        />
      </div>
    );
  };

  return (
    <div className="agriculture-gallery">
      {/* Header */}
      <div className="agriculture-header">
        <div className="agriculture-header-left">
          <div className="agriculture-header-icon">
            <FaTree />
          </div>
          <h1 className="agriculture-header-title">{t.headerTitle || 'Agriculture Gallery'}</h1>
        </div>
      </div>

      <div className="agriculture-container">
        {/* Sidebar */}
        <div className="agriculture-sidebar">
          <h3 className="agriculture-sidebar-title">{t.sidebarTitle || 'Categories'}</h3>
          <ul className="agriculture-category-list">
            {categories.map((category) => (
              <li
                key={category.key}
                className={`agriculture-category-item ${activeCategory === category.key ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.key)}
              >
                <span className="agriculture-category-icon">{category.icon}</span>
                <span className="agriculture-category-label">{getCategoryLabel(category.key)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Gallery */}
        <div className="agriculture-content">
          <div className="agriculture-content-header">
            <div className="agriculture-content-title">
              <span className="agriculture-title-icon">{currentContent.icon}</span>
              <h2>{currentContent.title}</h2>
              <span className="image-count">
                ({currentContent.images?.length || 0} {t.images || 'images'})
              </span>
            </div>
          </div>

          {activeCategory === 'videos' ? (
            currentContent.videos && currentContent.videos.length > 0 ? (
              <div className="agriculture-videos-grid">
                {currentContent.videos.map((video) => (
                  <div key={video.id} className="agriculture-video-card">
                    <div className="video-container">
                      <iframe
                        src={video.src}
                        title={video.title}
                        className="agriculture-gallery-video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-content-message">
                <div className="no-content-icon">
                  <FaVideo />
                </div>
                <p>{t.noVideos || 'No videos available'}</p>
              </div>
            )
          ) : (
            showSkeleton ? (
              <SkeletonLoader count={Math.min(currentContent.images?.length || 6, 6)} />
            ) : currentContent.images && currentContent.images.length > 0 ? (
              <div className="agriculture-images-grid">
                {currentContent.images.map((item, index) => (
                  <GalleryImage
                    key={item.id || index}
                    src={item.src}
                    alt={`${currentContent.title} - ${t.image || 'Image'} ${index + 1}`}
                    onClick={() => openLightbox(item, index)}
                  />
                ))}
              </div>
            ) : (
              <div className="no-content-message">
                <div className="no-content-icon">
                  {currentContent.icon}
                </div>
                <p>{t.noImages || 'No images available'}</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="close-lightbox" 
              onClick={closeLightbox}
              title={t.close || 'Close'}
              aria-label={t.close || 'Close'}
            >
              <FaTimes />
            </button>
            
            <img 
              src={selectedImage.src} 
              alt={t.fullView || 'Full view'} 
              className="lightbox-image"
              loading="lazy"
            />
            
            {currentContent.images && currentContent.images.length > 1 && (
              <div className="lightbox-nav">
                <button 
                  className="nav-btn" 
                  onClick={goToPrev}
                  title={t.prev || 'Previous'}
                  aria-label={t.prev || 'Previous'}
                >
                  <FaChevronLeft />
                </button>
                <button 
                  className="nav-btn" 
                  onClick={goToNext}
                  title={t.next || 'Next'}
                  aria-label={t.next || 'Next'}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgricultureGallery;